import { type ReactNode, useRef } from 'react'
import type { DialogRootActions } from '@base-ui/react/dialog'
import {
  Drawer,
  breakpointQueries,
  Button,
  InputGroup,
  Modal,
  useMediaQuery,
} from '@systemeio/components'
import { XCloseIcon, YoutubeIcon } from '@systemeio/icons'
import { useMutation } from '@tanstack/react-query'
import { cn } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import { Controller, useForm } from 'react-hook-form'
import { EMBEDDED_VIDEO_URL_MAX_LENGTH } from 'shared/constants'
import { useEmbeddedVideo } from 'modules/attachments/api/get-embedded-video'
import { usePostAttachmentsContext } from 'modules/attachments/components/context'
import {
  isEmbeddedVideoUrlTooLong,
  isValidEmbeddedVideoUrl,
} from 'modules/attachments/utils/attachment-utils'
import {
  AttachmentTypeEnum,
  AttachmentUploadStatusEnum,
} from 'modules/community/types/post-attachment'

interface EmbeddedVideoAttachmentProps {
  className?: string
  iconClassName?: string
}

const EmbeddedVideoAttachment = ({
  className,
  iconClassName,
}: EmbeddedVideoAttachmentProps): ReactNode => {
  const { t } = useTranslation()
  const actionsRef = useRef<DialogRootActions | null>(null)
  const isMobile = useMediaQuery(breakpointQueries.isMobile, { initializeWithValue: false })
  const form = useForm<{ url: string }>({
    mode: 'onChange',
    defaultValues: {
      url: '',
    },
  })

  const { addAttachment } = usePostAttachmentsContext()
  const metadata = useEmbeddedVideo({ url: form.watch('url').trim(), enabled: false })
  const mutation = useMutation({
    mutationFn: async () => {
      const { data } = await metadata.refetch({ throwOnError: true })
      if (!data) throw new Error('video metadata is unavailable')
      addAttachment({
        id: crypto.randomUUID(),
        status: AttachmentUploadStatusEnum.Success,
        url: form.getValues('url').trim(),
        previewUrl: data.thumbnail_url,
        type: AttachmentTypeEnum.EmbeddedVideo,
      })
    },
    retry: false,
    onSuccess: () => {
      actionsRef.current?.close()
    },
  })

  const urlError = form.formState.errors.url?.message
  const submitForm = form.handleSubmit(() => {
    if (!mutation.isPending) mutation.mutate()
  })
  const trigger = (
    <button data-testid="attach-video-link" type="button" className={className}>
      <YoutubeIcon
        className={cn('size-6 text-text-secondary hover:text-text-primary', iconClassName)}
      />
    </button>
  )

  const title = t('post.attachments.embedded_video_link.title')

  const fields = (
    <>
      <Controller
        control={form.control}
        name="url"
        rules={{
          validate: value => {
            const normalizedUrl = value.trim()

            if (isEmbeddedVideoUrlTooLong(normalizedUrl)) {
              return t('post.attachments.embedded_video_link.error_too_long', {
                max: EMBEDDED_VIDEO_URL_MAX_LENGTH,
              })
            }

            if (!isValidEmbeddedVideoUrl(normalizedUrl)) {
              return t('post.attachments.embedded_video_link.error_unsupported')
            }

            return true
          },
        }}
        render={({ field }) => (
          <div className="flex flex-col gap-1">
            <label data-testid="video-link-label" className="text-sm font-medium text-text-primary">
              {t('global.url')}
            </label>
            <InputGroup.Root data-invalid={!!urlError || undefined}>
              <InputGroup.Input
                type="url"
                disabled={mutation.isPending}
                value={field.value}
                onChange={e => {
                  field.onChange(e.target.value)
                  mutation.reset()
                }}
                onKeyDown={e => {
                  if (e.key !== 'Enter') {
                    return
                  }

                  e.preventDefault()
                  void submitForm()
                }}
              />
            </InputGroup.Root>
            {urlError && <p className="text-sm text-text-error">{urlError}</p>}
          </div>
        )}
      />
      {mutation.isError && (
        <p data-testid="video-link-request-error" className="text-sm text-text-error">
          {t('post.attachments.upload_failed')}
        </p>
      )}
      <span data-testid="video-link-description">
        {t('post.attachments.embedded_video_link.description')}
      </span>
    </>
  )

  const renderAction = (buttonClassName?: string): ReactNode => (
    <>
      <Button
        variant="secondary"
        data-testid="video-link-cancel"
        onClick={() => {
          actionsRef.current?.close()
        }}
        disabled={mutation.isPending}
        className={buttonClassName}
      >
        {t('confirmation_modal.cancel')}
      </Button>
      <Button
        variant="primary"
        isLoading={mutation.isPending}
        onClick={submitForm}
        data-testid="video-link-confirm"
        className={buttonClassName}
      >
        {t('confirmation_modal.confirm')}
      </Button>
    </>
  )

  const action = renderAction()

  return isMobile ? (
    <Drawer.Root
      actionsRef={actionsRef}
      onOpenChange={(open, details) => {
        if (open) return
        if (mutation.isPending && details.reason !== 'imperative-action') {
          details.cancel()
          return
        }
        form.reset()
        mutation.reset()
      }}
    >
      <Drawer.Trigger render={trigger} />
      <Drawer.Popup>
        <Drawer.Header className="flex-row items-start gap-2.5">
          <Drawer.Title className="min-w-0 flex-1">{title}</Drawer.Title>
          <Drawer.Close
            aria-label="Close"
            className="shrink-0"
            render={<Button variant="ghost-gray" size="icon-sm" />}
          >
            <XCloseIcon />
          </Drawer.Close>
        </Drawer.Header>
        <Drawer.Content className="flex flex-col gap-6">{fields}</Drawer.Content>
        <Drawer.Footer>
          <div className="flex w-full gap-3">{renderAction('min-w-[90px] flex-1')}</div>
        </Drawer.Footer>
      </Drawer.Popup>
    </Drawer.Root>
  ) : (
    <Modal.Root
      actionsRef={actionsRef}
      onOpenChange={(open, details) => {
        if (open) return
        if (mutation.isPending && details.reason !== 'imperative-action') {
          details.cancel()
          return
        }
        form.reset()
        mutation.reset()
      }}
    >
      <Modal.Trigger render={trigger} />
      <Modal.Popup>
        <Modal.Header>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-8 py-6">{fields}</div>
        <Modal.Footer>{action}</Modal.Footer>
      </Modal.Popup>
    </Modal.Root>
  )
}

export default EmbeddedVideoAttachment
